import React, { useState, useLayoutEffect, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../services/api";
import TextInputComponent from "../../components/textinput";
import ButtonComponent from "../../components/button";
import Spinner from "../../components/spinner";
import { useToaster } from "../../context/ToasterContext";
import "../../styles/payment-webview.scss";
import { ReactComponent as ExclamationCircleIcon } from "../../assets/icons/exclaimation-circle.svg";
import { ReactComponent as SuccessCircleFilledIcon } from "../../assets/icons/check-circle.svg";
import { ReactComponent as FailedCircleFilledIcon } from "../../assets/icons/close-circle.svg";
import { ReactComponent as DollarCircleIcon } from "../../assets/icons/dollar-circle.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/check-circle.svg";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/chevron-left.svg";
import { ReactComponent as PayForMeIcon } from "../../assets/icons/pay-for-me.svg";
import { ReactComponent as PaySelfMultiModeIcon } from "../../assets/icons/pay-self-multi-mode.svg";
import { ReactComponent as EqualSplitIcon } from "../../assets/icons/equal-split.svg";
import { ReactComponent as VariableSplitIcon } from "../../assets/icons/variable-split.svg";
import { fetchShopDetailsByShopLink, getOrderStatusApiCall, getListActivitiesApiCall, formatAmountInLocale, returnShortCodeOfPaymentMethod } from "../../utils/global";
import useGlobalState from "../../utils/global";
import useInitFcmRegisterProcess from "../../utils/registerFcmPushNotificationsForOrderActivities";
import useIfOrderSessionTimedout from "../../utils/checkIfOrderSessionTimedout";
import { useShopDetailsContext } from '../../context/ShopDetailsContext';
import PoweredByComponent from "../../webview-components/powered-by";
import CopyAndShareLinksComponent from "../../webview-components/copy-and-share-links";
import LiveActivitiesComponent from "../../webview-components/live-activities";
import PoolCompletedDiv from "../../webview-components/pool-completed-div";
import FcmNotificationPermissionPopup from "../../webview-components/fcm-notification-permission-popup";
import PaymentSessionTimedOutComponent from "../../webview-components/payment-session-timedout";
import MerchantLogoHeader from "../../webview-components/merchant-logo-header";

const SdkCheckout = () => {
  useEffect(() => {
    document.title = "Checkout - SuperCheckout";
  }, []);

  let { shop, id } = useParams();
  //console.log("useParams shopLink: " + shop);

  const [fcmTrigger, setFcmTrigger] = useState(null);

  const [searchParams] = useSearchParams();
  let shopifyShopName = searchParams.get("shop") || "";

  const { new_activity_sound, pool_completion_sound, set_confetti_play_init_bool, poolProgressBarPercent, setPoolProgressBarPercent, getListActivitiesResult, set_getListActivitiesResult, screenName, setScreenName, cartItemsPaidAlready, set_cartItemsPaidAlready, amountPaidSoFar, setAmountPaidSoFar, amountLeft, setAmountLeft, noOfSplitPaymentsDone, setNoOfSplitPaymentsDone, isOrderTimeSensitive, set_isOrderTimeSensitive, orderTimedOut, set_orderTimedOut, this_orderIsRefundedBool, set_this_orderIsRefundedBool } = useGlobalState(id, 'sdk-checkout-init');

  let this_copyAndShareLinkUrl = window.location.href;

  const [copyAndShareLinkUrl, set_copyAndShareLinkUrl] = useState(this_copyAndShareLinkUrl);

  const { toaster } = useToaster();
  const navigate = useNavigate();

  const [noOfPax, setNoOfPax] = useState(null);
  const [shopDetails, setShopDetails] = useState({});

  const [selectedOrderType, set_selectedOrderType] = useState("");
  const [getOrderStatusResult, set_getOrderStatusResult] = useState([]);

  const [noOfPax_custom, setNoOfPax_custom] = useState();
  const increment_noOfPax = () => setNoOfPax_custom(prevCount => Math.min(25, (prevCount ?? 10) + 1));
  const decrement_noOfPax = () => setNoOfPax_custom(prevCount => Math.max(11, (prevCount ?? 11) - 1));

  const [sdkCartItemQty, setSdkCartItemQty] = useState();
  const [sdkCartItemQty_custom, setSdkCartItemQty_custom] = useState();
  const increment_sdkCartItemQty = () => setSdkCartItemQty_custom(prevCount => Math.min(25, (prevCount ?? 10) + 1));
  const decrement_sdkCartItemQty = () => setSdkCartItemQty_custom(prevCount => Math.max(11, (prevCount ?? 11) - 1));

  useEffect(() => {
    if(noOfPax_custom !== null){
      setNoOfPax(noOfPax_custom);
    }
  }, [noOfPax_custom]);

  useEffect(() => {
    if(noOfPax < 11){
      setNoOfPax_custom(null);
    }
  }, [noOfPax]);

  useEffect(() => {
    if(selectedOrderType === ''){
      setShopDetailsContext(prev => ({
        ...prev,
        order_type: ""
      }));
    }
    else{
      setShopDetailsContext(prev => ({
        ...prev,
        order_type: "selecting"
      }));
    }
  }, [selectedOrderType]);
  
  const [currentOrderData, set_currentOrderData] = useState([]);
  const [cartDisplayData, set_cartDisplayData] = useState([]);

  const [loading, setLoading] = useState(true);
  const { setShopDetailsContext } = useShopDetailsContext();

  //fetching shop details by shoplink
  useEffect(() => {
    const fetchShopDetails = async () => {
      try {
        const response = await fetchShopDetailsByShopLink(id);

        setShopDetails(response);

        setFcmTrigger({ id: id, shop: response.shop_name });

        if(typeof response.meta_data?.timeout !== 'undefined' && response.meta_data?.timeout !== null && response.meta_data?.timeout !== 0){
          set_isOrderTimeSensitive(true);
        }

        //checking if only 1 payment option is enabled - start
        if(response.meta_data.pay_for_me === true && response.meta_data.multi_mode_single_pax === false && response.meta_data.fixed_price === false && response.meta_data.variable_price === false){
          set_selectedOrderType('fixed_payforme');
        }
        else if(response.meta_data.pay_for_me === false && response.meta_data.multi_mode_single_pax === true && response.meta_data.fixed_price === false && response.meta_data.variable_price === false){
          set_selectedOrderType('variable_self');
        }
        else if(response.meta_data.pay_for_me === false && response.meta_data.multi_mode_single_pax === false && response.meta_data.fixed_price === true && response.meta_data.variable_price === false){
          set_selectedOrderType('fixed_price');
        }
        else if(response.meta_data.pay_for_me === false && response.meta_data.multi_mode_single_pax === false && response.meta_data.fixed_price === false && response.meta_data.variable_price === true){
          set_selectedOrderType('variable_price');
        }
        //checking if only 1 payment option is enabled - end

        setShopDetailsContext(prev => ({
          ...prev,
          user_image: response.user_image,
          shop_name: response.shop_name,
          meta_data: response.meta_data
        }));

        getOrderDetailsApiCall();
      } catch (err) {
        console.log(err);
      }
    };

    fetchShopDetails();
  }, []);

  const getOrderDetailsApiCall = async () => {
    try {
      setLoading(true);

      let createOrderApiCallResponse = await api.get(
        `sdk/orders/get-order-data/${id}/1`
      );
      console.log(createOrderApiCallResponse);

      //setLoading(false);

      if (
        (createOrderApiCallResponse.status === 200 ||
          createOrderApiCallResponse.status === 201) &&
        createOrderApiCallResponse.data
      ) {
        if (createOrderApiCallResponse.data) {
          for(var i=0; i < createOrderApiCallResponse.data.cart_data.cart_display.length; i++){
            createOrderApiCallResponse.data.cart_data.cart_display[i]['selected_quantity'] = createOrderApiCallResponse.data.cart_data.cart_display[i].quantity;
          }

          set_currentOrderData(createOrderApiCallResponse.data);

          if(shopifyShopName === '' || shopifyShopName === null){
            if(createOrderApiCallResponse.data.cart_data.cart_display.length > 1){
              set_selectedOrderType("fixed_cart");
            }
            else if(createOrderApiCallResponse.data.cart_data.cart_display.length === 1){
              if(createOrderApiCallResponse.data.cart_data.cart_display[0].quantity > 1){
                set_selectedOrderType("fixed_cart");
              }
            }
          }
        }
      } else {
        if (
          createOrderApiCallResponse.data.errorMessage &&
          createOrderApiCallResponse.data.errorMessage.length
        ) {
          toaster.error(createOrderApiCallResponse.data.errorMessage);
        } else {
          toaster.error("Something went wrong");
        }
      }
    } catch (error) {
      setLoading(false);
      
      console.log("Exception: ", error);

      toaster.error("Something went wrong");
    }
  };

  //upon change in currentOrderData
  useEffect(() => {
    if(currentOrderData && currentOrderData.cart_data){
      set_cartDisplayData(currentOrderData.cart_data.cart_display);

      const getOrderStatusApiCall_init = async (id) => {
        try {
          const response = await getOrderStatusApiCall(id);

          setShopDetailsContext(prev => ({
            ...prev,
            order_created_at: response.created_at,
            order_type: response.order_type
          }));

          set_getOrderStatusResult(response);

          setPoolProgressBarPercent(response.percentage_done);

          if(typeof response.pax !== 'undefined' && response.pax !== null && response.pax !== '' && response.pax !== 0){
            setNoOfPax(response.pax);
          }

          let amount_so_far = parseFloat(response.total_amount) - parseFloat(response.amount_left);
          setAmountPaidSoFar(parseFloat(amount_so_far).toFixed(2));
          setAmountLeft(parseFloat(response.amount_left).toFixed(2));

          if(response.percentage_done === 100 || response.status === 'completed'){
            setScreenName("pool_completed");

            //window.manager.addConfetti();
            //set_confetti_play_init_bool(true);
          }
          else if(response.order_type !== 'None' && selectedOrderType !== 'fixed_cart'){
            setScreenName("order_in_progress_already");
          }
          /* else if(response.order_type !== 'None' && currentOrderData.ask_pax === true){
            setScreenName("order_in_progress_already");
          } */
  
          getListActivitiesApiCall_init(id);
        } catch (err) {
          console.log(err);
        }
      };
      getOrderStatusApiCall_init(id);

      const getListActivitiesApiCall_init = async (id) => {
        try {
          const response = await getListActivitiesApiCall(id);
  
          setLoading(false);

          set_getListActivitiesResult(response.activities);

          let is_refunded = false;
          let paid_items = [];
          for(let i=0; i < response.activities.length; i++){
            if(response.activities[i].order_status === 'refunded'){
              set_this_orderIsRefundedBool(true);
              is_refunded = true;
            }

            if(response.activities[i].order_status === 'authorized'){
              setNoOfSplitPaymentsDone((prev) => prev + 1);
              
              for(var j=0; j < response.activities[i].items_status.event_list.length; j++){
                response.activities[i].items_status.event_list[j]['user_name'] = response.activities[i].user_name;
              }

              paid_items.push(...response.activities[i].items_status.event_list);
            }
          }

          if(response.status === 'completed' && is_refunded === false){
            window.manager.addConfetti();
            set_confetti_play_init_bool(true);
          }

          if(cartItemsPaidAlready.length === 0){
            set_cartItemsPaidAlready(paid_items);
          }
          else{
            set_cartItemsPaidAlready((prev) => [paid_items, ...prev]);
          }
        } catch (err) {
          console.log(err);
        }
      };
    }
  }, [currentOrderData]);

  useEffect(() => {
    let this_orderType = selectedOrderType;
    if(typeof getOrderStatusResult !== 'undefined' && getOrderStatusResult !== null){
      if(typeof getOrderStatusResult.order_type !== 'undefined' && getOrderStatusResult.order_type !== null && getOrderStatusResult.order_type !== 'None' && getOrderStatusResult.order_type !== ''){
        this_orderType = getOrderStatusResult.order_type;
      }
    }
    else if(typeof currentOrderData !== 'undefined' && currentOrderData !== null){
      if(typeof currentOrderData.order_type !== 'undefined' && currentOrderData.order_type !== null && currentOrderData.order_type !== 'None' && currentOrderData.order_type !== ''){
        this_orderType = currentOrderData.order_type;
      }
    }

    if(this_orderType !== 'fixed_cart'){
      let total_pax = noOfPax;
      if(this_orderType === 'fixed_payforme'){
        total_pax = "1";
      }
      else if(this_orderType === 'variable_self'){
        total_pax = "-1";
      }
      else if(this_orderType === 'variable_price'){
        total_pax = "0";
      }
      else if(this_orderType === 'fixed_price'){
        total_pax = noOfPax;
      }

      console.log(this_orderType);
      console.log(noOfPax);
      console.log(total_pax);

      if(typeof currentOrderData !== 'undefined'){
        set_copyAndShareLinkUrl(window.location.origin +"/"+ returnShortCodeOfPaymentMethod(currentOrderData.payment_method) +"/"+ id +"?"+ (shopifyShopName !== "" ? "shop=" + shopifyShopName + "&" : "") +"total_pax="+ total_pax);
      }
    }
  }, [noOfPax, selectedOrderType, currentOrderData, getOrderStatusResult]);

  useEffect(() => {
    if(cartItemsPaidAlready.length > 0 && cartDisplayData.length > 0){
      const updatedCart = cartDisplayData.map((item) => {
        let newItem = { ...item };
  
        if (newItem.item_ids.length > 0) {
          cartItemsPaidAlready.forEach((paidItem) => {
            const index = newItem.item_ids.indexOf(paidItem.item_id);
            if (index > -1) {
              newItem.item_ids = [...newItem.item_ids]; // copy before mutation
              newItem.item_ids.splice(index, 1);
              //newItem.quantity = newItem.quantity - 1;
              newItem.selected_quantity = newItem.selected_quantity - 1;
            }
          });
        }
  
        return newItem;
      });
  
      set_cartDisplayData(updatedCart);
    }
  }, [cartItemsPaidAlready]);

  const proceedToCheckoutPage = (pay_for_me_bool, pay_by_me_multiMode) => {
    let pax = "0";

    if(pay_for_me_bool === true){
      setNoOfPax(1);

      pax = "1";
    }
    else if(pay_by_me_multiMode === true){
      setNoOfPax(-1);

      pax = "-1";
    }

    if(selectedOrderType === 'fixed_price'){
      if((noOfPax === '' || noOfPax === null || noOfPax === 0) && pay_for_me_bool !== true && pay_by_me_multiMode !== true){
        toaster.error("Please select the number of people paying together!");
  
        return;
      }
      else{
        pax = noOfPax;
      }
    }

    setShopDetailsContext(prev => ({
      ...prev,
      order_type: selectedOrderType
    }));

    if(shopifyShopName !== ''){
      navigate("/"+ returnShortCodeOfPaymentMethod(currentOrderData.payment_method) +"/"+ id +"?shop="+ shopifyShopName +"&total_pax="+ pax);
    }
    else{
      navigate("/"+ returnShortCodeOfPaymentMethod(currentOrderData.payment_method) +"/"+ id +"?total_pax="+ pax);
    }
  };

  const proceedWithPayForMe = () => {
    set_selectedOrderType('fixed_payforme');

    proceedToCheckoutPage(true, false);
  }

  const handleChange_sdkCartItemQty = (id, type) => {
    set_cartDisplayData((prevProducts) =>
      prevProducts.map((cartItem) =>
        cartItem.cart_token === id
          ? {
              ...cartItem,
              selected_quantity:
                type === "increase"
                  ? Math.min(cartItem.selected_quantity + 1, cartItem.quantity)
                  : Math.max(cartItem.selected_quantity - 1, 1),
            }
          : cartItem
      )
    );
  };

  const cart_method_proceed_to_payment = () => {
    let selected_cartItems_array = [];

    const selectedItems = cartDisplayData
      .map((item) => {
        const checkbox = document.getElementById(`singleCartLineItemCheckbox_${item.cart_token}`);
        const quantity = document.getElementById(`sdkCartItemQty_customText_${item.cart_token}`);

        if(checkbox?.checked){
          for(var i=0; i < item.item_ids.length; i++){
            for(var j=0; j < Number(quantity.value); j++){
              selected_cartItems_array.push(item.item_ids[j]);
            }
            break;
          }
        }
      })
      .filter(Boolean); //Remove null values

    console.log(selected_cartItems_array);

    if(selected_cartItems_array.length === 0){
      toaster.error("Please select at least 1 item!");
    }
    else{
      if(shopifyShopName !== ''){
        navigate("/"+ returnShortCodeOfPaymentMethod(currentOrderData.payment_method) +"/"+ id +"?shop="+ shopifyShopName +"&cart_items="+ selected_cartItems_array.join());
      }
      else{
        navigate("/"+ returnShortCodeOfPaymentMethod(currentOrderData.payment_method) +"/"+ id +"?cart_items="+ selected_cartItems_array.join());
      }
    }
  }

  const renderCartItem_disabledStateOrNot = (this_cartItemData) => {
    if(typeof this_cartItemData !== 'undefined'){
      if(this_cartItemData.name === "Product A"){
        //console.log(this_cartItemData);
        //console.log(cartItemsPaidAlready);
      }

      let return_val = "";

      if(this_cartItemData.item_ids.length === 0){
        return_val = "soldOut";
      }
      else{
        for(var j=0; j < cartItemsPaidAlready.length; j++){
          if(this_cartItemData.item_ids.length > 0){
            if(this_cartItemData.cart_token === cartItemsPaidAlready[j].cart_token){
              return_val = "partially_soldOut";
            }
          }
        }
      }
      
      return return_val;
    }
  }

  const renderCartItem_PaidByNames = (this_cartItemData) => {
    let return_arr = [];

    for(var j=0; j < cartItemsPaidAlready.length; j++){
      if(this_cartItemData.cart_token === cartItemsPaidAlready[j].cart_token){
        return_arr.push(cartItemsPaidAlready[j].user_name);
      }
    }
    
    if (return_arr.length === 1) {
      return return_arr[0];
    } else if (return_arr.length === 2) {
      return return_arr.join(" & ");
    } else if (return_arr.length > 2) {
      return return_arr.slice(0, -1).join(", ") + " & " + return_arr[return_arr.length - 1];
    }
  }

  const goBackToPreviousScreen = () => {
    if(selectedOrderType === ""){
      //
    }
    else{
      set_selectedOrderType('');
    }
  }

  useInitFcmRegisterProcess(fcmTrigger?.id, fcmTrigger?.shop);

  const orderSessionTimedout_bool = useIfOrderSessionTimedout(getOrderStatusResult?.created_at, shopDetails?.meta_data?.timeout);
  if(orderSessionTimedout_bool === true && orderTimedOut !== true && isOrderTimeSensitive === true && screenName !== "pool_completed"){
    set_orderTimedOut(orderSessionTimedout_bool);
  }

  return (
    <div className="payment-page-wrapper">
      {loading && <Spinner />}

      {!loading && 
        <>
          {orderTimedOut === true && 
            <div>
              <PaymentSessionTimedOutComponent></PaymentSessionTimedOutComponent>
            </div>
          }
          {orderTimedOut !== true && 
            <div>
              <div className="mt-2 mb-3">
                <MerchantLogoHeader shop_name={shopDetails.shop_name} user_image={shopDetails.user_image}></MerchantLogoHeader>
              </div>

              {screenName !== "pool_completed" && screenName !== "order_in_progress_already" &&
                <div className="payment-form-wrapper">
                  <div className="form-wrapper text-start">
                    <div className="typeform-container pt-0">
                      {selectedOrderType !== 'fixed_cart' &&
                        <form id="pax-form">
                          <div>
                            {getOrderStatusResult.order_type === 'None' && selectedOrderType !== '' &&
                              <div id="backButton_arrow" className="backButton_arrow" onClick={() => goBackToPreviousScreen()}>
                                <ChevronLeftIcon />
                              </div>
                            }

                            <div className="text-center">
                              <p className="text-color-amount-label">Total amount</p>
                              {!loading && <h4 className="text-color-amount-heading bigSize">{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,currentOrderData.total_amount)}</h4>}
                            </div>

                            <div className="selectOrderType_fixedSplit_or_variableSplit_container">
                              {selectedOrderType === "" &&
                                <div className="selectOrderType_container">
                                  <div className="">
                                    <div className="mt-4 mb-0">
                                      <label className="form-label noMarginBottom">Select how you want to split</label>

                                      <div className="ordertype-cards-container mt-2">
                                        {shopDetails.meta_data.pay_for_me === true &&
                                          <div className="d-flex align-items-center mb-3 single-ordertype-card" id="fixed_payforme_type" onClick={() => proceedWithPayForMe()}>
                                            <div className="iconArea me-3">
                                              <PayForMeIcon />
                                            </div>
                                            <div className="flex-grow-1">
                                              <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                  <strong>Pay on my behalf</strong>
                                                </h6>
                                              </div>
                                              <p className="note">Let someone else pay for you</p>
                                            </div>
                                          </div>
                                        }
                                        {shopDetails.meta_data.multi_mode_single_pax === true &&
                                          <div className="d-flex align-items-center mb-3 single-ordertype-card" id="variable_self_type" onClick={() => set_selectedOrderType('variable_self')}>
                                            <div className="iconArea me-3">
                                              <PaySelfMultiModeIcon />
                                            </div>
                                            <div className="flex-grow-1">
                                              <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                  <strong>Stack & Pay</strong>
                                                </h6>
                                              </div>
                                              <p className="note">When one card isn't enough — Stack & Pay</p>
                                            </div>
                                          </div>
                                        }
                                        {shopDetails.meta_data.fixed_price === true &&
                                          <div className="d-flex align-items-center mb-3 single-ordertype-card" id="fixed_price_type" onClick={() => set_selectedOrderType('fixed_price')}>
                                            <div className="iconArea me-3">
                                              <EqualSplitIcon />
                                            </div>
                                            <div className="flex-grow-1">
                                              <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                  <strong>Share evenly</strong>
                                                </h6>
                                              </div>
                                              <p className="note">Everyone pays the same -- no hassle</p>
                                            </div>
                                          </div>
                                        }
                                        {shopDetails.meta_data.variable_price === true &&
                                          <div className="d-flex align-items-center mb-3 single-ordertype-card" id="variable_price_type" onClick={() => set_selectedOrderType('variable_price')}>
                                            <div className="iconArea me-3">
                                              <VariableSplitIcon />
                                            </div>
                                            <div className="flex-grow-1">
                                              <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                  <strong>Split by share</strong>
                                                </h6>
                                              </div>
                                              <p className="note">Each person pays their own share</p>
                                            </div>
                                          </div>
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              }
                              {selectedOrderType === "fixed_price" &&
                                <div className="fixedSplit_container whiteCard mt-5">
                                  <div className="input-wrapper mb-0">
                                    <div className="mt-0 mb-3">
                                      <label className="form-label noMarginBottom">How many people are sharing the payment?</label>

                                      <div className="row text-center g-2 noOfPax-cards-container">
                                        <div className="col-2">
                                          <div id="noOfPax_singleCard_2" className={`single-no-pax-container ${
                                            noOfPax === 2 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(2)}>
                                            2
                                          </div>
                                        </div>
                                        <div className="col-2">
                                          <div id="noOfPax_singleCard_3" className={`single-no-pax-container ${
                                            noOfPax === 3 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(3)}>
                                            3
                                          </div>
                                        </div>
                                        <div className="col-2">
                                          <div id="noOfPax_singleCard_4" className={`single-no-pax-container ${
                                            noOfPax === 4 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(4)}>
                                            4
                                          </div>
                                        </div>
                                        <div className="col-2">
                                          <div id="noOfPax_singleCard_5" className={`single-no-pax-container ${
                                            noOfPax === 5 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(5)}>
                                            5
                                          </div>
                                        </div>
                                        <div id="noOfPax_singleCard_6" className="col-2">
                                          <div className={`single-no-pax-container ${
                                            noOfPax === 6 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(6)}>
                                            6
                                          </div>
                                        </div>
                                        <div id="noOfPax_singleCard_7" className="col-2">
                                          <div className={`single-no-pax-container ${
                                            noOfPax === 7 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(7)}>
                                            7
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row text-center g-2 noOfPax-cards-container">
                                        <div className="col-2">
                                          <div id="noOfPax_singleCard_8" className={`single-no-pax-container ${
                                            noOfPax === 8 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(8)}>
                                            8
                                          </div>
                                        </div>
                                        <div className="col-2">
                                          <div id="noOfPax_singleCard_9" className={`single-no-pax-container ${
                                            noOfPax === 9 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(9)}>
                                            9
                                          </div>
                                        </div>
                                        <div id="noOfPax_singleCard_10" className="col-2">
                                          <div className={`single-no-pax-container ${
                                            noOfPax === 10 ? `active` : ""
                                          }`} onClick={() => setNoOfPax(10)}>
                                            10
                                          </div>
                                        </div>
                                        <div className="col-6">
                                          <div className="">
                                            <div className="input-group noOfPax_custom_inputGroup mb-3">
                                              <button className="btn btn-primary" type="button" id="noOfPax_customBtn_minus" onClick={decrement_noOfPax}>-</button>

                                              <input type="text" id="noOfPax_customText" className="form-control" readOnly placeholder="Eg.: 15" value={noOfPax_custom ?? ""} onChange={(e) => {
                                                setNoOfPax(e.target.value);
                                              }} />

                                              <button className="btn btn-primary" type="button" id="noOfPax_customBtn_plus" onClick={increment_noOfPax}>+</button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <ButtonComponent
                                      type="button"
                                      id="get_payment_link_btn"
                                      className="btn btn-primary btn-pool-shadow btn-pool-full"
                                      label="Start sharing"
                                      variant="primary"
                                      size="md"
                                      onClick={() => proceedToCheckoutPage()}
                                    ></ButtonComponent>
                                  </div>
                                </div>
                              }
                              {selectedOrderType === "variable_price" &&
                                <div className="variableSplit_container">
                                  <div className="whiteCard mt-5">
                                    <div className="input-wrapper mb-0">
                                      <div className="mt-0 mb-4">
                                        <p className="note">Ready to pay your share? Click below, enter your amount, and you're done.</p>
                                      </div>

                                      <ButtonComponent
                                        type="button"
                                        id="variable_split_get_payment_link_btn"
                                        className="btn btn-primary btn-pool-shadow btn-pool-full"
                                        label="Start sharing"
                                        variant="primary"
                                        size="md"
                                        onClick={() => proceedToCheckoutPage()}
                                      ></ButtonComponent>
                                    </div>
                                  </div>
                                </div>
                              }
                              {selectedOrderType === "fixed_payforme" &&
                                <div className="payForMe_container">
                                  <div className="input-wrapper mb-0">
                                    <div className="mt-5 mb-3 text-center">
                                      <p className="note">Your <b>Pay for Me</b> link is ready. Share it with someone who'll cover this for you.</p>
                                    </div>

                                    <CopyAndShareLinksComponent order_type="fixed_payforme" link_url={copyAndShareLinkUrl} view_mode="inline"></CopyAndShareLinksComponent>
                                  </div>
                                </div>
                              }
                              {selectedOrderType === "variable_self" &&
                                <div className="stackAndPay_container">
                                  <div className="whiteCard mt-5">
                                    <div className="input-wrapper mb-0">
                                        <div className="mt-0 mb-3">
                                          <p className="note">Use more than one card? No problem. Click below to Stack & Pay.</p>

                                          {shopifyShopName !== '' &&
                                            <p className="note mt-2 fw-semibold text-danger">
                                              <ExclamationCircleIcon className="align-middle" />
                                              {' '}
                                              <span className="align-middle">Stack up to 2 payment methods — it's a duet, not a band.</span>
                                            </p>
                                          }

                                          <hr />
                                        </div>

                                        <div className="stackAndPay_whiteCard_detailArea mb-4">
                                          <div className="row justify-content-between">
                                            <div className="col-6">
                                              <p className="smallLabel">Amount</p>
                                              <p className="labelValue">{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,currentOrderData.total_amount)}</p>
                                            </div>
                                            <div className="col-6 text-end">
                                              <DollarCircleIcon className="svgIcon" />
                                            </div>
                                          </div>
                                          <hr />
                                          <div className="row justify-content-between">
                                            <div className="col-6">
                                              <p className="smallLabel">Paying to</p>
                                              <p className="labelValue">{shopDetails.shop_name}</p>
                                            </div>
                                            <div className="col-6 text-end">
                                              <PayForMeIcon className="svgIcon" />
                                            </div>
                                          </div>
                                          <hr />
                                          <div className="row justify-content-between">
                                            <div className="col-6">
                                              <p className="smallLabel">Method</p>
                                              <p className="labelValue">Stack & Pay</p>
                                            </div>
                                            <div className="col-6 text-end">
                                              <PaySelfMultiModeIcon className="svgIcon" />
                                            </div>
                                          </div>
                                        </div>

                                        <ButtonComponent
                                          type="button"
                                          id="variable_self_link_btn"
                                          className="btn btn-primary btn-pool-shadow btn-pool-full"
                                          label='Start stacking'
                                          variant="primary"
                                          size="md"
                                          onClick={() => proceedToCheckoutPage(false,true)}
                                        ></ButtonComponent>
                                      </div>
                                  </div>
                                </div>
                              }
                            </div>
                          </div>
                        </form>
                      }
                      {selectedOrderType === 'fixed_cart' &&
                        <div className="cartBasedCheckoutContainer">
                          <ul className="list-group">
                            {cartDisplayData.length > 0 && cartDisplayData.map((singleCartLineItem, index) => (
                              <li className={`list-group-item singleCartLineItemContainer p-3 ${renderCartItem_disabledStateOrNot(singleCartLineItem)}`} key={singleCartLineItem.cart_token}>
                                {singleCartLineItem.cart_token !== 'discount' && 
                                  <div className="row align-items-center">
                                    <div className="col-auto" style={{ width: "40px" }}>
                                      <input className="form-check-input me-1" type="checkbox" value={singleCartLineItem.cart_token} name="singleCartLineItemCheckbox" id={`singleCartLineItemCheckbox_${singleCartLineItem.cart_token}`} />
                                    </div>
                                    <div className="col-auto" style={{ width: "75px" }}>
                                      <img src={singleCartLineItem.image_link || "https://placehold.co/100"} className="rounded cartItemImage" />
                                    </div>
                                    <div className="col" style={{ width: "calc(100% - 40px - 75px)" }}>
                                      <div className="d-flex w-100 justify-content-between">
                                        <h6 className="mb-1">
                                          <span>{singleCartLineItem.name} x {singleCartLineItem.quantity}</span>
                                        </h6>
                                        <small className="amountText text-end">
                                          <span>{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,singleCartLineItem.price / singleCartLineItem.quantity)}</span>
                                        </small>
                                      </div>
                                      {/* <p className="mb-1">
                                        <span>{singleCartLineItem.description}</span>
                                      </p> */}
                                      {/* <small>
                                        <span>x {singleCartLineItem.quantity}</span>
                                      </small> */}

                                      {renderCartItem_disabledStateOrNot(singleCartLineItem) !== 'soldOut' &&
                                        <div className="input-group sdkCartItemQty_custom_inputGroup">
                                          <button className="btn btn-primary" type="button" id="sdkCartItemQty_customBtn_minus" onClick={() => handleChange_sdkCartItemQty(singleCartLineItem.cart_token, "decrease")} disabled={singleCartLineItem.selected_quantity <= 1}>-</button>

                                          <input type="text" id={`sdkCartItemQty_customText_${singleCartLineItem.cart_token}`} name="sdkCartItemQty_customText" className="form-control" readOnly value={singleCartLineItem.selected_quantity} />

                                          <button className="btn btn-primary" type="button" id="sdkCartItemQty_customBtn_plus" onClick={() => handleChange_sdkCartItemQty(singleCartLineItem.cart_token, "increase")} disabled={singleCartLineItem.selected_quantity >= singleCartLineItem.item_ids.length}>+</button>
                                        </div>
                                      }
                                      {renderCartItem_disabledStateOrNot(singleCartLineItem) === 'soldOut' &&
                                        <div>
                                          <div>
                                            <span className="badge text-bg-success">
                                              <CheckCircleIcon /> Paid
                                            </span>
                                          </div>
                                          <p className="note mt-2">Paid by: {renderCartItem_PaidByNames(singleCartLineItem)}</p>
                                        </div>
                                      }
                                      {renderCartItem_disabledStateOrNot(singleCartLineItem) === 'partially_soldOut' &&
                                        <div>
                                          <p className="note mt-2">Partially paid by: {renderCartItem_PaidByNames(singleCartLineItem)}</p>
                                        </div>
                                      }
                                    </div>
                                  </div>
                                }
                                {singleCartLineItem.cart_token === 'discount' && 
                                  <div className="row">
                                    <div className="col">
                                      <div className="d-flex w-100 justify-content-between">
                                        <h6 className="mb-0">
                                          <span>Discount</span>
                                        </h6>
                                        <small>
                                          <span>{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,singleCartLineItem.total_price)}</span>
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                }
                              </li>
                            ))}

                            <li className="list-group-item p-3">
                              <div className="row">
                                <div className="col">
                                  <div className="d-flex w-100 justify-content-between">
                                    <h6 className="mb-0">
                                      <span>Shipping</span>
                                    </h6>
                                    <small>
                                      <span>{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,currentOrderData.total_shipping)}</span>
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </li>
                            <li className="list-group-item p-3">
                              <div className="row">
                                <div className="col">
                                  <div className="d-flex w-100 justify-content-between">
                                    <h6 className="mb-0">
                                      <span>Tax</span>
                                    </h6>
                                    <small>
                                      <span>{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,currentOrderData.total_tax)}</span>
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </li>
                            <li className="list-group-item p-3">
                              <div className="row">
                                <div className="col">
                                  <div className="d-flex w-100 justify-content-between">
                                    <h6 className="mb-0">
                                      <span>Total discount (on order)</span>
                                    </h6>
                                    <small>
                                      <span>{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,currentOrderData.total_discount)}</span>
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </li>
                            <li className="list-group-item p-3">
                              <div className="row">
                                <div className="col">
                                  <div className="d-flex w-100 justify-content-between">
                                    <h5 className="mb-0">
                                      <span>Total amount</span>
                                    </h5>
                                    <h5 className="mb-0">
                                      <span>{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,currentOrderData.total_amount)}</span>
                                    </h5>
                                  </div>
                                </div>
                              </div>
                            </li>
                          </ul>

                          <div className="input-wrapper mt-3 mb-5">
                            <ButtonComponent
                              type="button"
                              id="cart_method_proceed_to_payment_btn"
                              className="btn btn-primary btn-pool-shadow btn-pool-full"
                              label="Proceed to payment"
                              variant="primary"
                              size="md"
                              onClick={() => cart_method_proceed_to_payment()}
                            ></ButtonComponent>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }

              {screenName === "order_in_progress_already" &&
                <div>
                  <div className="payment-form-wrapper">
                    <div className="form-wrapper text-start">
                      <div className="text-center mb-3">
                        <div className="payment-success-container">
                          <div className="success-icon mb-2">
                            Payment in progress...
                          </div>
                        </div>
                      </div>

                      <div className="text-center mb-4">
                        <p className="text-color-amount-label">Total amount</p>
                        {!loading && <h4 className="text-color-amount-heading bigSize">{shopDetails.currency_with_symbol}{formatAmountInLocale(shopDetails.user_currency,currentOrderData.total_amount)}</h4>}
                      </div>
                    </div>
                  </div>

                  <FcmNotificationPermissionPopup id={id} shop={shop}></FcmNotificationPermissionPopup>
                </div>
              }

              {screenName === "pool_completed" &&
                <div>
                  <div className="payment-form-wrapper">
                    <div className="form-wrapper text-start">
                      <PoolCompletedDiv this_orderIsRefundedBool={this_orderIsRefundedBool}></PoolCompletedDiv>
                    </div>
                  </div>
                </div>
              }
              
              {(screenName === "pool_completed" || screenName === "order_in_progress_already") &&
                <div className="payment-form-wrapper mb-5">
                  <div className="form-wrapper text-start">
                    <LiveActivitiesComponent
                      id={id}
                      screenName={screenName}
                      order_type={selectedOrderType === 'fixed_payforme' ? selectedOrderType : getOrderStatusResult.order_type}
                      currentOrderData={currentOrderData}
                      poolProgressBarPercent={poolProgressBarPercent}
                      noOfSplitPaymentsDone={noOfSplitPaymentsDone}
                      noOfPax={noOfPax}
                      shopDetails={shopDetails}
                      amountPaidSoFar={amountPaidSoFar}
                      amountLeft={amountLeft}
                      getListActivitiesResult={getListActivitiesResult}
                      this_orderIsRefundedBool={this_orderIsRefundedBool}
                    ></LiveActivitiesComponent>
                  </div>
                </div>
              }

              <PoweredByComponent></PoweredByComponent>

              {screenName !== "pool_completed" && getOrderStatusResult.order_type !== 'variable_self' && (selectedOrderType === 'fixed_cart' || screenName === "order_in_progress_already") &&
                <CopyAndShareLinksComponent order_type={getOrderStatusResult.order_type} link_url={copyAndShareLinkUrl}></CopyAndShareLinksComponent>
              }
            </div>
          }
        </>
      }
    </div>
  );
};

export default SdkCheckout;
